import React, { useEffect, useMemo, useState } from 'react';
import CampaignProvider from '../components/CampaignProvider/CampaignProvider';
import { SocialStatusProvider } from '../context/SocialStatusContext';
import { CapabilityProvider, useCapabilities } from '../capabilities/CapabilityContext';
import MobileApp from '../components/Mobile/MobileApp.jsx';
import { apiFetch, parseResponseBody } from '../utils/http';

const BROWSER_PROOF_PARAM = 'browserProof';
const BROWSER_PROOF_VALUE = 'mobile-capability';
const DEFAULT_SCREEN = 'aistorage';

const PROBE_DEFINITIONS = [
  {
    id: 'overview',
    label: 'analytics overview',
    endpoint: '/api/v1/analytics/overview',
    options: { method: 'GET' },
  },
  {
    id: 'engagement',
    label: 'analytics engagement',
    endpoint: '/api/v1/analytics/engagement?period=30d&platform=all',
    options: { method: 'GET' },
  },
  {
    id: 'report-generate',
    label: 'analytics report generate',
    endpoint: '/api/v1/analytics/reports/generate',
    options: {
      method: 'POST',
      data: {
        report_type: 'performance',
        format: 'pdf',
      },
    },
  },
];

const readParams = () => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search || '');
};

export const shouldRenderBrowserProof = () => {
  if (process.env.REACT_APP_ENABLE_BROWSER_PROOF !== 'true') {
    return false;
  }

  return readParams().get(BROWSER_PROOF_PARAM) === BROWSER_PROOF_VALUE;
};

const getInitialScreen = () => {
  const screen = readParams().get('screen');
  return screen || DEFAULT_SCREEN;
};

const formatCapability = (capability) => {
  if (!capability) {
    return 'missing';
  }

  const reasonCode = capability.entry?.reason?.code || capability.reason || 'none';
  const coverage = capability.entry?.coverage?.status || 'n/a';
  return `${capability.state} | reason=${reasonCode} | coverage=${coverage}`;
};

const ProofPanel = ({ initialScreen }) => {
  const { status, error, getRouteStateForScreen, getNavigationStateForScreen } = useCapabilities();
  const [probeState, setProbeState] = useState({
    loading: true,
    items: [],
    error: null,
  });

  const capabilitySummary = useMemo(() => ({
    analyticsRoute: getRouteStateForScreen('analytics'),
    aiStorageRoute: getRouteStateForScreen('aistorage'),
    imageGeneratorRoute: getRouteStateForScreen('image-generator'),
    aiStorageNav: getNavigationStateForScreen('aistorage'),
    imageGeneratorNav: getNavigationStateForScreen('image-generator'),
  }), [getNavigationStateForScreen, getRouteStateForScreen]);

  useEffect(() => {
    let cancelled = false;

    const runProbes = async () => {
      const shouldProbeAnalytics = readParams().get('probeAnalytics') !== '0';
      if (!shouldProbeAnalytics) {
        setProbeState({
          loading: false,
          items: [],
          error: null,
        });
        return;
      }

      try {
        const items = [];

        for (const definition of PROBE_DEFINITIONS) {
          const response = await apiFetch(definition.endpoint, definition.options);
          const body = await parseResponseBody(response);
          items.push({
            id: definition.id,
            label: definition.label,
            httpStatus: response.status,
            contract: response.headers.get('X-Loopino-Contract'),
            degradedState: response.headers.get('X-Loopino-Degraded-State'),
            degradedCoverage: response.headers.get('X-Loopino-Degraded-Coverage'),
            payloadStatus: body?.status || null,
            reasonCode: body?.degraded?.reason?.code || null,
          });
        }

        if (!cancelled) {
          setProbeState({
            loading: false,
            items,
            error: null,
          });
        }
      } catch (probeError) {
        if (!cancelled) {
          setProbeState({
            loading: false,
            items: [],
            error: probeError.message || 'analytics_probe_failed',
          });
        }
      }
    };

    runProbes();

    return () => {
      cancelled = true;
    };
  }, []);

  const panelStyle = {
    fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    color: '#d9e3f0',
    background: 'rgba(8, 13, 20, 0.92)',
    border: '1px solid rgba(120, 148, 176, 0.28)',
    borderRadius: 18,
    padding: '16px 18px',
    margin: '16px',
    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.32)',
  };

  const valueStyle = {
    color: '#f7fbff',
    wordBreak: 'break-word',
  };

  return (
    <section data-proof="panel" style={panelStyle}>
      <div style={{ display: 'grid', gap: 8 }}>
        <div data-proof="requested-screen">
          requested_screen: <strong style={valueStyle}>{initialScreen}</strong>
        </div>
        <div data-proof="capability-load-status">
          capability_status: <strong style={valueStyle}>{status}</strong>
        </div>
        <div data-proof="capability-load-error">
          capability_error: <strong style={valueStyle}>{error?.message || 'none'}</strong>
        </div>
        <div data-proof="analytics-route-state">
          analytics_route: <strong style={valueStyle}>{formatCapability(capabilitySummary.analyticsRoute)}</strong>
        </div>
        <div data-proof="aistorage-route-state">
          aistorage_route: <strong style={valueStyle}>{formatCapability(capabilitySummary.aiStorageRoute)}</strong>
        </div>
        <div data-proof="image-generator-route-state">
          image_generator_route: <strong style={valueStyle}>{formatCapability(capabilitySummary.imageGeneratorRoute)}</strong>
        </div>
        <div data-proof="aistorage-nav-state">
          aistorage_nav: <strong style={valueStyle}>{formatCapability(capabilitySummary.aiStorageNav)}</strong>
        </div>
        <div data-proof="image-generator-nav-state">
          image_generator_nav: <strong style={valueStyle}>{formatCapability(capabilitySummary.imageGeneratorNav)}</strong>
        </div>
        <div data-proof="analytics-probe-status">
          analytics_probe_loading: <strong style={valueStyle}>{String(probeState.loading)}</strong>
        </div>
        <div data-proof="analytics-probe-error">
          analytics_probe_error: <strong style={valueStyle}>{probeState.error || 'none'}</strong>
        </div>
        {probeState.items.map((item) => (
          <div
            key={item.id}
            data-proof={`analytics-probe-${item.id}`}
          >
            {item.id}: <strong style={valueStyle}>
              http={item.httpStatus} payload={item.payloadStatus || 'none'} state={item.degradedState || 'none'} coverage={item.degradedCoverage || 'none'} reason={item.reasonCode || 'none'} contract={item.contract || 'none'}
            </strong>
          </div>
        ))}
      </div>
    </section>
  );
};

const BrowserProofSurface = () => {
  const initialScreen = getInitialScreen();

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top, #163047 0%, #081019 48%, #05070b 100%)',
        paddingBottom: 32,
      }}
    >
      <ProofPanel initialScreen={initialScreen} />
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: '0 16px 24px',
          boxSizing: 'border-box',
        }}
      >
        <div
          data-proof="mobile-shell"
          style={{
            width: '100%',
            maxWidth: 430,
            minHeight: 820,
            borderRadius: 28,
            overflow: 'hidden',
            border: '1px solid rgba(143, 173, 201, 0.25)',
            boxShadow: '0 30px 90px rgba(0, 0, 0, 0.45)',
            background: '#000',
          }}
        >
          <MobileApp initialScreen={initialScreen} />
        </div>
      </div>
    </div>
  );
};

const BrowserProofApp = () => (
  <CapabilityProvider surfaceVariant="mobile" enabled>
    <SocialStatusProvider>
      <CampaignProvider>
        <BrowserProofSurface />
      </CampaignProvider>
    </SocialStatusProvider>
  </CapabilityProvider>
);

export default BrowserProofApp;
